import { Popover, PopoverContent, PopoverTrigger } from '@chakra-ui/react';
import { DateOverviewIcon, MemberIcon, ProjectDetailsIcon } from '@sketchnote/icons';
import { Avatar, AvatarGroup, Calendar } from '@sketchnote/shared-components';
import cn from 'classnames';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useFileStore } from 'src/features/files/store/useFileStore';
import { getDifferenceInDays, isStartDateGreater } from 'src/utils/date-time-functions';

export const ProjectDetails = ({
  setDaysDiff,
}: {
  setDaysDiff: Dispatch<SetStateAction<number>>;
}) => {
  const { onUpdateOverviewField } = useFileStore();
  const router = useRouter();

  const [showStartDateSelector, setShowStartDateSelector] = useState(false);
  const [showEndDateSelector, setShowEndDateSelector] = useState(false);
  const [showMemberDropdrown, setShowMemberDropdrown] = useState(false);
  const [showGuestsDropdrown, setShowGuestsDropdrown] = useState(false);

  const currentFile = useFileStore(
    useCallback((s) => s.files[router.query.slug as string], [router.query.slug as string])
  );

  const members = currentFile?.workspace?.members?.filter((obj) => obj.role !== 'guest') || [];
  const guests = currentFile?.workspace?.members?.filter((obj) => obj.role === 'guest') || [];

  const [startDate, setStartDate] = useState(
    currentFile?.overview?.start_date ? new Date(currentFile?.overview?.start_date) : new Date()
  );
  const [endDate, setEndDate] = useState(
    currentFile?.overview?.end_date ? new Date(currentFile?.overview?.end_date) : new Date()
  );

  useEffect(() => {
    currentFile &&
      currentFile?.overview &&
      currentFile?.overview?.start_date !== undefined &&
      currentFile?.overview?.end_date !== undefined &&
      setDaysDiff(
        getDifferenceInDays(currentFile?.overview?.start_date, currentFile?.overview?.end_date)
      );
  }, [currentFile?.overview?.start_date, currentFile?.overview?.end_date]);

  const toggleStartDate = () => {
    setShowStartDateSelector(!showStartDateSelector);
  };

  const toggleEndDate = () => {
    setShowEndDateSelector(!showEndDateSelector);
  };

  const updateDate = (type) => {
    type === 'start_date' ? setShowStartDateSelector(false) : setShowEndDateSelector(false);
    currentFile &&
      currentFile.id &&
      onUpdateOverviewField({
        fileId: currentFile.id,
        type,
        value: type === 'start_date' ? startDate : endDate,
      });
  };

  const onSetDate = (dates, type) => {
    type === 'start-date' ? setStartDate(dates[0]) : setEndDate(dates[0]);
  };

  return (
    <div className="flex flex-col px-20 my-20">
      <section className="flex items-center mb-10">
        <ProjectDetailsIcon />
        <label className="text-gray-500 text-gray-999 ml-8 text-md">Project details</label>
      </section>
      <section className="flex flex-col justify-center text-gray-500 ml-6">
        {/* <div className="flex justify-between items-center mb-12">
          <label className="text-sm items-center">
            <AdminIcon className="mr-6" />
            Admin
          </label>
          <Avatar
            key={`${currentFile?.owner?.memberId}`}
            name={currentFile?.owner?.firstname || ' '}
            type={currentFile?.owner?.avatar ? 'photo' : 'initials'}
            src={currentFile?.owner?.avatar}
            size="sm"
            tooltip={true}
          />
        </div> */}
        <div className="flex justify-between items-center mb-12">
          <label className="flex text-sm items-center">
            <MemberIcon className="mr-6" />
            Members
          </label>
          <div className="flex">
            <AvatarGroup maxCount={1}>
              {members?.map((member, index) => (
                <Avatar
                  key={`${member.memberId}-${index}`}
                  wrapperClassName="-mr-4 border-2"
                  name={member.firstname}
                  size="sm"
                  src={member.avatar}
                  type={member.avatar ? 'photo' : 'initials'}
                  tooltip={true}
                />
              ))}
            </AvatarGroup>
            {members.length > 2 && (
              <Popover>
                <PopoverTrigger>
                  <button onClick={() => setShowMemberDropdrown(true)}>
                    <Avatar
                      size="sm"
                      name={members.length - 2}
                      type={'initials'}
                      src={''}
                      tooltip={false}
                      wrapperClassName="-ml-4 border-1 border-white  transform transition duration-500 hover:scale-125"
                    />
                  </button>
                </PopoverTrigger>
                <PopoverContent isOpen={showMemberDropdrown}>
                  <UserList users={members} />
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>
        {guests.length > 0 && (
          <div className="flex justify-between items-center mb-12">
            <label className="flex text-sm items-center">
              <MemberIcon className="mr-6" />
              Guests
            </label>
            <div className="flex">
              <AvatarGroup maxCount={1}>
                {guests?.map((guest, index) => (
                  <Avatar
                    key={`${guest.memberId}-${index}`}
                    wrapperClassName="-mr-4 border-2"
                    name={guest.firstname}
                    size="sm"
                    src={guest.avatar}
                    type={guest.avatar ? 'photo' : 'initials'}
                    tooltip={true}
                  />
                ))}
              </AvatarGroup>
              {guests.length > 2 && (
                <Popover>
                  <PopoverTrigger>
                    <button onClick={() => setShowGuestsDropdrown(true)}>
                      <Avatar
                        size="sm"
                        name={guests.length - 2}
                        tooltip={false}
                        type={'initials'}
                        src={''}
                        wrapperClassName="-ml-4 border-1 border-white  transform transition duration-500 hover:scale-125"
                      />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent isOpen={showGuestsDropdrown}>
                    <UserList users={guests} />
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>
        )}
        <div className="flex justify-between items-center mb-12">
          <label className="flex text-sm items-center">
            <DateOverviewIcon className="mr-6" />
            Start date
          </label>
          <div>
            {startDate && (
              <Popover isOpen={showStartDateSelector} onClose={toggleStartDate}>
                <PopoverTrigger>
                  <div onClick={toggleStartDate} className="text-sm">
                    {format(startDate, 'dd/MM/yyyy')}
                  </div>
                </PopoverTrigger>
                <PopoverContent>
                  <Calendar
                    value={[startDate] as [Date]}
                    show={['date']}
                    onChange={(e) => {
                      onSetDate(e as [Date], 'start-date');
                      if (endDate !== undefined && isStartDateGreater(e as [Date], endDate)) {
                        onSetDate(e as [Date], 'end-date');
                      }
                    }}
                    onDone={() => {
                      toggleStartDate();
                      updateDate('start_date');
                      updateDate('end_date');
                    }}
                    initialTab={'date'}
                    showReminder={false}
                    reminderOffset={0}
                    showRecurring={false}
                    disableEndDate={true}
                    endDateFirst={false}
                  />
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>

        <div className="flex justify-between items-center">
          <label className="flex text-sm items-center">
            <DateOverviewIcon className="mr-6" />
            End date
          </label>
          <div>
            {endDate && (
              <Popover isOpen={showEndDateSelector} onClose={toggleEndDate}>
                <PopoverTrigger>
                  <div
                    onClick={toggleEndDate}
                    className={cn('text-sm', {
                      'pointer-events-none': currentFile?.overview?.start_date === undefined,
                    })}
                  >
                    {format(endDate, 'dd/MM/yyyy')}
                  </div>
                </PopoverTrigger>

                <PopoverContent>
                  <Calendar
                    value={[endDate] as [Date]}
                    show={['date']}
                    onChange={(e) => {
                      onSetDate(e as [Date], 'end-date');
                      if (isStartDateGreater(startDate, e as [Date])) {
                        onSetDate(e as [Date], 'start-date');
                      }
                    }}
                    onDone={() => {
                      toggleEndDate();
                      updateDate('end_date');
                      updateDate('start_date');
                    }}
                    initialTab={'date'}
                    showReminder={false}
                    reminderOffset={0}
                    showRecurring={false}
                    disableEndDate={true}
                    endDateFirst={false}
                  />
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

const UserList = ({ users }: { users: any }) => {
  return (
    <div
      className="shadow-md rounded-md bg-gray-100"
      style={{
        maxHeight: '180px',
        width: '150px',
        overflow: 'auto',
      }}
    >
      {users.slice(2).map((option, index) => {
        return (
          <div
            key={`{${option.memberId}-${index}}`}
            className="flex gap-8 px-10 h-40 items-center min-w-2150 bg-gray-100 shadow-md"
          >
            <img
              src={option.avatar}
              alt={option.name}
              className="h-20 w-20 rounded-full object-cover flex-shrink-0"
              style={{ border: '1px solid #D5D7DD' }}
            />
            <p className="text-sm font-regular text-gray-999 leading-20 truncate">
              {option.firstname}
            </p>
          </div>
        );
      })}
    </div>
  );
};
